import { ReactNode, useEffect, useMemo } from 'react';
import { useLocation } from 'wouter';
import { useAuth, UserRole } from '@/hooks/useAuth';

interface RouteGuardProps {
  children: ReactNode;
  allowedRoles?: UserRole[];
  publicOnly?: boolean;
}

function normalizeRole(role: unknown): UserRole {
  const normalized = typeof role === 'string' ? role.toLowerCase() : '';

  if (normalized === 'tutor' || normalized === 'student' || normalized === 'admin') {
    return normalized;
  }

  return 'student';
}

function getRoleHome(role: UserRole): string {
  switch (role) {
    case 'tutor':
      return '/tutor/dashboard';
    case 'student':
      return '/student/dashboard';
    case 'admin':
      return '/';
    default:
      return '/';
  }
}

function LoadingFallback() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center text-text-dark">
      Loading...
    </div>
  );
}

export function RouteGuard({ children, allowedRoles, publicOnly = false }: RouteGuardProps) {
  const [location, navigate] = useLocation();
  const { isAuthenticated, isLoading, user } = useAuth();

  const redirectTarget = useMemo(() => {
    if (isLoading) {
      return null;
    }

    if (publicOnly) {
      if (isAuthenticated && user) {
        return getRoleHome(normalizeRole(user.role));
      }

      return null;
    }

    if (!isAuthenticated || !user) {
      const currentPath = `${window.location.pathname}${window.location.search}`;
      return `/login?redirect=${encodeURIComponent(currentPath)}`;
    }

    const userRole = normalizeRole(user.role);

    if (allowedRoles && !allowedRoles.includes(userRole)) {
      return getRoleHome(userRole);
    }

    return null;
  }, [isLoading, publicOnly, isAuthenticated, user, allowedRoles]);

  useEffect(() => {
    if (!redirectTarget || location === redirectTarget) {
      return;
    }

    navigate(redirectTarget);
  }, [redirectTarget, location, navigate]);

  if (isLoading || redirectTarget) {
    return <LoadingFallback />;
  }

  return <>{children}</>;
}
